import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { LayoutService } from '../../layout/service/layout-service.service';
import { environment } from '@env';
import { KeycloakService } from 'keycloak-angular';
import * as Sentry from "@sentry/angular";
import {
  AuthGuard,
  BillingAccessService,
  clearKeycloakTokenWithoutLogin,
  clearStoredImpersonationSession,
  hasStoredImpersonationSession,
  markStoredImpersonationSessionEnded,
  shouldBlockImpersonationFallback,
} from '@apruv-client/services';
import { firstValueFrom } from 'rxjs';
import { VerifyAnalyticsAccessService } from './verify-analytics-access.service';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  // Keep track of redirects to prevent loops
  private redirectCount = 0;
  private MAX_REDIRECTS = 2;

  constructor(
    private router: Router,
    private layoutService: LayoutService,
    private keycloak: KeycloakService,
    private authGuard: AuthGuard, // Inject to ensure Keycloak is initialized
    private verifyAnalyticsAccessService: VerifyAnalyticsAccessService,
    private billingAccessService: BillingAccessService
  ) {}

  private logDebug(message?: any, ...optionalParams: any[]): void {
    if (environment.production) {
      return;
    }
    console.log(message, ...optionalParams);
  }

  private denyAccess(state: RouterStateSnapshot, extra: Record<string, unknown> = {}): false {
    if (this.redirectCount < this.MAX_REDIRECTS) {
      this.redirectCount++;
      this.logDebug(`❌ RoleGuard: Redirecting to access-denied (redirect #${this.redirectCount}) for route: ${state.url}`);
      this.router.navigate(['/access-denied']);
    } else {
      console.warn('RoleGuard: Maximum access-denied redirects reached, preventing redirect loop');
      Sentry.captureMessage('Access denied redirect loop prevented', {
        level: 'warning',
        tags: {
          url: state.url,
          redirect_count: this.redirectCount
        },
        extra
      });
    }

    return false;
  }

  private endStoredImpersonationSession(): false {
    markStoredImpersonationSessionEnded();
    clearStoredImpersonationSession();
    clearKeycloakTokenWithoutLogin(this.keycloak.getKeycloakInstance());
    this.router
      .navigateByUrl('/access-denied?reason=impersonation-ended')
      .catch(() => {});
    return false;
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.logDebug('🛡️ RoleGuard activated for:', state.url);

    // Reset redirect counter when navigating to non-access-denied pages
    if (!state.url.startsWith('/access-denied')) {
      this.redirectCount = 0;
    }

    // Allow Keycloak OAuth callback URLs to pass through for processing
    // These contain state/code/session_state parameters from Keycloak auth flow
    const hash = window.location.hash;
    if (hash && (hash.includes('state=') || hash.includes('code=') || hash.includes('session_state='))) {
      this.logDebug('✅ RoleGuard: Allowing Keycloak OAuth callback to be processed');
      return true;
    }

    // Always allow access to the access-denied page itself, registration, and verification pages
    const isPublicRoute =
      state.url.startsWith('/access-denied') ||
      state.url.startsWith('/registration') ||
      state.url.startsWith('/review') ||
      state.url.startsWith('/v1/verify/verification') ||
      state.url.startsWith('/v2/verify/verification') ||
      state.url.includes('verifyLink=true') ||
      window.location.href.includes('verifyLink=true');

    if (isPublicRoute) {
      this.logDebug('✅ RoleGuard: Allowing access to verification/access-denied page');
      return true;
    }

    if (shouldBlockImpersonationFallback()) {
      this.router
        .navigateByUrl('/access-denied?reason=impersonation-ended')
        .catch(() => {});
      return false;
    }

    // Detect special URL patterns for development environments
    const isDevelopmentAsset = state.url.includes('/@fs/') || 
                               state.url.includes('vite/deps/') || 
                               state.url.includes('?v=');

    // Always allow Vite development module requests
    if (isDevelopmentAsset) {
      this.logDebug('✅ RoleGuard: Allowing development asset');
      return true;
    }
    
    // Always allow module loading for authenticated users
    // Module requests should not be blocked as they're assets, not route access
    const isModuleRequest = state.url.includes('.js') || 
                           state.url.includes('/chunk-') || 
                           state.url.includes('/assets/') ||
                           state.url.includes('primeng_');
    
    if (isModuleRequest) {
      // Always allow module/asset loading regardless of permissions
      // Permission checks should happen at route level, not module level
      this.logDebug('✅ RoleGuard: Allowing module/asset request');
      return true;
    }
    
    // Wait for Keycloak to be initialized by AuthGuard
    this.logDebug('🔐 RoleGuard: Waiting for Keycloak to be ready...');
    try {
      // Ensure AuthGuard has initialized Keycloak first
      const initialized = await this.authGuard.ensureInitialized();
      if (!initialized && shouldBlockImpersonationFallback()) {
        return false;
      }

      const isLoggedIn = await this.keycloak.isLoggedIn();
      this.logDebug('🔐 RoleGuard: Keycloak ready, isLoggedIn:', isLoggedIn);

      // If logged in but the bootstrap has not populated access yet, resolve it
      // from billing. Keycloak remains the authentication/token provider only.
      if (isLoggedIn && !this.layoutService.permissions) {
        this.logDebug('📊 RoleGuard: Loading billing access...');
        try {
          const access = await this.billingAccessService.load();
          this.layoutService.permissions = [
            ...access.permissions,
            ...access.appRoles,
          ];
          this.layoutService.role = access.role;
          this.layoutService.userType = access.userType;
          this.layoutService.superuser =
            this.layoutService.permissions?.includes('super_user') === true;
          this.layoutService.orgs = access.legacyOrgs;
          this.layoutService.setBillingLocationScope(
            access.locationsByOrganization
          );
        } catch (e) {
          console.error('Error loading billing access:', e);
          Sentry.captureException(e);
        }
      }

      // Get permissions (should be available now)
      const userPermissions = this.layoutService.permissions;
      const isSuperUser = userPermissions?.includes('super_user') === true;
      this.logDebug('🛡️ RoleGuard: User permissions:', userPermissions);
      
      // For non-module requests without permissions, redirect to login
      if(!userPermissions) {
        if (hasStoredImpersonationSession()) {
          return this.denyAccess(state, {
            reason: 'missing-impersonation-permissions',
          });
        }

        if (shouldBlockImpersonationFallback()) {
          this.router
            .navigateByUrl('/access-denied?reason=impersonation-ended')
            .catch(() => {});
          return false;
        }

        this.logDebug('❌ RoleGuard: No user permissions, redirecting to login');
        try {
          // Store current URL for post-login redirect
          if (state.url && state.url !== '/' && !state.url.startsWith('/access-denied')) {
            sessionStorage.setItem('redirectAfterLogin', state.url);
          }
          
          // Redirect to login page
          this.keycloak.login({ redirectUri: window.location.origin + state.url });
        } catch (error) {
          console.error('Login redirect failed:', error);
          Sentry.captureException(error);
        }
        return false;
      }

      const allowedUserTypes: string[] = route.data['allowedUserTypes'] || [];
      const allowedUserRoles: string[] = route.data['allowedUserRoles'] || [];
      const allowedCompanyIds: number[] = route.data['allowedCompanyIds'] || [];
      const selectedCompanyId = this.layoutService.companySelected?.id
        ?? this.layoutService.getCompanyId()
        ?? Number(sessionStorage.getItem('companyId') || 0);
      const currentUserType = this.layoutService.userType;
      const currentUserRole = this.layoutService.role;
      const requiresVerifyAnalyticsAccess = route.data['verifyAnalyticsAccess'] === true;

      if (requiresVerifyAnalyticsAccess && !isSuperUser) {
        const hasVerifyAnalyticsAccess = await firstValueFrom(
          this.verifyAnalyticsAccessService.hasAccessForCurrentUser()
        );

        this.logDebug('🛡️ RoleGuard: Verify analytics access check result:', {
          hasVerifyAnalyticsAccess,
          currentUserType,
          currentUserRole,
          selectedCompanyId
        });

        if (!hasVerifyAnalyticsAccess) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `Verify analytics access denied for route: ${state.url}`,
            level: 'warning',
            data: {
              url: state.url,
              currentUserType,
              currentUserRole,
              selectedCompanyId
            }
          });

          return this.denyAccess(state, {
            verifyAnalyticsAccess: true,
            currentUserType,
            currentUserRole,
            selectedCompanyId
          });
        }
      }

      if (!isSuperUser && (allowedUserTypes.length || allowedUserRoles.length || allowedCompanyIds.length)) {
        const hasAllowedIdentity = (
          (!allowedUserTypes.length && !allowedUserRoles.length)
          || (!!currentUserType && allowedUserTypes.includes(currentUserType))
          || (!!currentUserRole && allowedUserRoles.includes(currentUserRole))
        );
        const hasAllowedCompany = !allowedCompanyIds.length || allowedCompanyIds.includes(selectedCompanyId);

        this.logDebug('🛡️ RoleGuard: Custom access check result:', {
          hasAllowedIdentity,
          hasAllowedCompany,
          allowedUserTypes,
          allowedUserRoles,
          allowedCompanyIds,
          currentUserType,
          currentUserRole,
          selectedCompanyId
        });

        if (!hasAllowedIdentity || !hasAllowedCompany) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `Custom access denied for route: ${state.url}`,
            level: 'warning',
            data: {
              url: state.url,
              allowedUserTypes,
              allowedUserRoles,
              allowedCompanyIds,
              currentUserType,
              currentUserRole,
              selectedCompanyId
            }
          });

          return this.denyAccess(state, {
            allowedUserTypes,
            allowedUserRoles,
            allowedCompanyIds,
            currentUserType,
            currentUserRole,
            selectedCompanyId
          });
        }
      }
      
      // Check if super user, allow navigation
      if (isSuperUser) {
        this.logDebug('✅ RoleGuard: Super user access granted');
        return true;
      }

      // For non-root paths, check allowed roles
      const allowedRoles: string[] = route.data['allowedRoles'] || [];
      if (!allowedRoles || allowedRoles.length === 0) {
        return true;
      }

      // Check if any user role starts with one of the allowed roles
      const hasAccess = allowedRoles.some((allowedRole) =>
        userPermissions?.some((userPermission) => userPermission && userPermission.startsWith(allowedRole))
      );
      
      this.logDebug('🛡️ RoleGuard: Role check result:', { hasAccess, allowedRoles, userPermissions });

      if (!hasAccess) {
        // Log permission denial for monitoring
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Access denied for route: ${state.url}`,
          level: 'warning',
          data: {
            url: state.url,
            allowedRoles,
            userPermissions: userPermissions || [],
            redirectCount: this.redirectCount
          }
        });

        return this.denyAccess(state, {
          allowedRoles,
          userPermissions: userPermissions || []
        });
      }

      return hasAccess;
      
    } catch (error) {
      console.error('🚨 RoleGuard: Error during authentication check:', error);
      Sentry.captureException(error);
      return false;
    }
  }
}
